
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Stack,
    Paper,
    Container,
    useTheme,
    useMediaQuery,
    alpha,
    IconButton,
    InputAdornment,
    Drawer,
    Divider,
    Chip,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import dayjs from 'dayjs';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useGetJobServiceQuery } from '../../../../features/OverViewAuth/overViewApi';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { selectPendingService, setPendingService } from '../../../../features/PostCreat/postcreateSlice';
import { FaChevronLeft, FaCheck, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import LivePriceCalculator from '../../../../components/Booking-Form/LivePriceCalculator';

interface ServiceDateSelectionProps {
    onNext: (data: { serviceId: string; serviceName: string; fromDate: string; toDate: string; fromTime: string; toTime: string; subModality?: string; cleaningType?: string; supplies?: string }) => void;
    onBack?: () => void;
}

const CustomCalendarHeader = (props: any) => {
    const { currentMonth, onMonthChange, minDate, maxDate } = props;

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const currentYear = currentMonth.year();
    const currentMonthIndex = currentMonth.month();

    const startYear = minDate ? dayjs(minDate).year() : currentYear - 10;
    const endYear = maxDate ? dayjs(maxDate).year() : currentYear + 10;

    const years = [];
    for (let i = startYear; i <= endYear; i++) {
        years.push(i);
    }

    const handleMonthChange = (event: any) => {
        onMonthChange(currentMonth.month(event.target.value), 'left');
    };

    const handleYearChange = (event: any) => {
        onMonthChange(currentMonth.year(event.target.value), 'left');
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Select
                    size="small"
                    value={currentMonthIndex}
                    onChange={handleMonthChange}
                    sx={{
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': { py: 0.5, px: 1 },
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.1)' }
                    }}
                >
                    {months.map((month, index) => (
                        <MenuItem key={month} value={index}>{month}</MenuItem>
                    ))}
                </Select>
                <Select
                    size="small"
                    value={currentYear}
                    onChange={handleYearChange}
                    sx={{
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': { py: 0.5, px: 1 },
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.1)' }
                    }}
                >
                    {years.map((year) => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                </Select>
            </Box>
            <Box>
                <IconButton size="small" onClick={() => onMonthChange(currentMonth.subtract(1, 'month'), 'right')}>
                    <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => onMonthChange(currentMonth.add(1, 'month'), 'left')}>
                    <ChevronRight fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
};

const ServiceDateSelection: React.FC<ServiceDateSelectionProps> = ({ onNext, onBack }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const pendingService = useAppSelector(selectPendingService);

    const { data: getservice, isLoading } = useGetJobServiceQuery({});

    const [services, setServices] = useState<any[]>([]);
    const [selectedService, setSelectedService] = useState<string>('');
    const [selectedServiceName, setSelectedServiceName] = useState<string>('');
    const [subModality, setSubModality] = useState<string>('');
    const [cleaningType, setCleaningType] = useState<string>('');
    const [supplies, setSupplies] = useState<string>('No');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerOption, setDrawerOption] = useState<string | null>(null);

    const selectedServiceDetails = services.find(s => s._id === selectedService);
    const pricingConfiguration = selectedServiceDetails?.pricingConfiguration || null;
    const subModalityOptions = pricingConfiguration?.subModality
        ? Object.keys(pricingConfiguration.subModality)
        : [];
    const cleaningTypeOptions = pricingConfiguration?.yes && typeof pricingConfiguration.yes === 'object'
        ? Object.keys(pricingConfiguration.yes).map((key: string) => key.replace(/\b\w/g, (char: string) => char.toUpperCase()))
        : [];
    const hasSubModalityPricing = subModalityOptions.length > 0;
    const hasComplexPricing = cleaningTypeOptions.length > 0;
    const hasSimpleSupplyPricing = pricingConfiguration && typeof pricingConfiguration.yes === 'number' && typeof pricingConfiguration.no === 'number';

    // Helper to get local date string YYYY-MM-DD
    const getLocalDateString = (date?: Date) => {
        const d = date || new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getMinDate = () => {
        const now = new Date();
        const currentHours = now.getHours();

        let minDate = new Date();

        // Buffer + Rounding check
        const bufferHours = 2; // Buffer
        let startHour = currentHours + bufferHours;
        if (now.getMinutes() > 0) startHour++;

        if (startHour >= 24) {
            minDate.setDate(minDate.getDate() + 1);
        }

        return getLocalDateString(minDate);
    };

    // Calculate initial times (first available 15-minute slot)
    const getInitialTimes = (dateStr: string) => {
        const safeLimit = dayjs().add(2, 'hour');

        // Round safeLimit up to the next 15-minute interval
        const safeMinute = Math.ceil(safeLimit.minute() / 15) * 15;
        let fromHour = safeLimit.hour();
        let fromMinute = safeMinute;
        if (fromMinute >= 60) {
            fromHour = (fromHour + 1) % 24;
            fromMinute = 0;
        }

        const from = `${String(fromHour).padStart(2, '0')}:${String(fromMinute).padStart(2, '0')}`;
        const fromSlot = dayjs(`${dateStr} ${from}`);
        const toSlot = fromSlot.add(2, 'hour');
        const to = `${String(toSlot.hour()).padStart(2, '0')}:${String(toSlot.minute()).padStart(2, '0')}`;

        return { from, to };
    };
    const getMaxDate = () => {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 2); // Max 2 years in future
        return getLocalDateString(d);
    };

    const minDate = getMinDate();
    const maxDate = getMaxDate();
    const initialTimes = getInitialTimes(minDate);

    const [fromDate, setFromDate] = useState<string>(minDate);
    const [toDate, setToDate] = useState<string>(minDate);
    const [fromTime, setFromTime] = useState<string>(initialTimes.from);
    const [toTime, setToTime] = useState<string>(initialTimes.to);

    useEffect(() => {
        const safeLimit = dayjs().add(2, 'hour');
        const currentSelected = dayjs(`${fromDate} ${fromTime}`);

        // If selected time is invalid (passed or within buffer), reset it
        if (currentSelected.isBefore(safeLimit)) {
            const { from, to } = getInitialTimes(fromDate);
            setFromTime(from);
            setToTime(to);
        }
    }, [fromDate]);

    // Helper to add days to a date string
    const addDays = (dateStr: string, days: number) => {
        const [y, m, d] = dateStr.split('-').map(Number);
        const dateObj = new Date(y, m - 1, d);
        dateObj.setDate(dateObj.getDate() + days);
        return getLocalDateString(dateObj);
    };

    const handleFromTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newFromTime = e.target.value;

        let validFromTime = newFromTime;

        // Enforce 2-hour lead time
        const safeLimit = dayjs().add(2, 'hour');
        const selectedDt = dayjs(`${fromDate} ${newFromTime}`);

        if (selectedDt.isBefore(safeLimit)) {
            const { from: minSafeTime } = getInitialTimes(fromDate);
            validFromTime = minSafeTime;
        }

        setFromTime(validFromTime);

        // Check if current service is cleaning/housekeeping
        const isCleaningOrHousekeeping = ['cleaning', 'housekeeping', 'cleaner', 'maid'].some(keyword =>
            selectedServiceName.toLowerCase().includes(keyword)
        );

        // Calculate ToTime
        if (validFromTime) {
            const [hours, minutes] = validFromTime.split(':').map(Number);
            // Default 2 hours, which is also the enforced duration for cleaning
            let newToHours = hours + 2;
            let nextDay = false;
            // Handle day rollover if needed
            if (newToHours >= 24) {
                newToHours = newToHours - 24;
                nextDay = true;
            }

            const newToTime = `${String(newToHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
            setToTime(newToTime);
        }
    };

    useEffect(() => {
        if (getservice?.data) {
            setServices(getservice.data);
        }
    }, [getservice]);

    const handleServiceChange = (event: any) => {
        const serviceId = event.target.value;

        const service = services.find(s => s._id === serviceId);
        // Salon bookings are temporarily frozen — block selection (also guards
        // the landing-page pre-select which calls this handler).
        if (service && /salon/i.test(service.name || '')) {
            toast.error("Salon bookings are coming soon.");
            return;
        }
        setSelectedService(serviceId);

        if (service) {
            setSelectedServiceName(service.name);
            const config = service.pricingConfiguration || null;
            const nextSubOptions = config?.subModality ? Object.keys(config.subModality) : [];
            const nextCleaningOptions = config?.yes && typeof config.yes === 'object'
                ? Object.keys(config.yes).map((key: string) => key.replace(/\w/g, (char: string) => char.toUpperCase()))
                : [];
            setSubModality(nextSubOptions[0] || '');
            setCleaningType(nextCleaningOptions[0] || '');

            // If switching TO cleaning/housekeeping, ensure 2-hour gap happens immediately
            const isCleaningOrHousekeeping = ['cleaning', 'housekeeping', 'cleaner', 'maid'].some(keyword =>
                service.name.toLowerCase().includes(keyword)
            );

            if (isCleaningOrHousekeeping && fromTime) {
                const [hours, minutes] = fromTime.split(':').map(Number);
                let newToHours = hours + 2;
                if (newToHours >= 24) newToHours -= 24;
                const newToTime = `${String(newToHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                setToTime(newToTime);
            }
        }
    };

    // Pre-select the service chosen on the landing page. The value comes from the
    // URL (?service=) for logged-in users, or from the store for users who had to
    // log in first (the query string is dropped by the auth redirect).
    useEffect(() => {
        if (!services.length || selectedService) return;

        const requested =
            new URLSearchParams(window.location.search).get('service') || pendingService;

        if (!requested) return;

        const normalize = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const target = normalize(requested);

        const match = services.find((s) => {
            const name = normalize(s.name);
            return name === target || name.includes(target) || target.includes(name);
        });

        if (match) {
            handleServiceChange({ target: { value: match._id } });
        }
        if (pendingService) {
            dispatch(setPendingService(''));
        }
    }, [services, pendingService]);

    const handleNext = () => {
        // Salon bookings are temporarily frozen — final backstop before proceeding.
        const selName = services.find(s => s._id === selectedService)?.name || selectedServiceName;
        if (/salon/i.test(selName || '')) {
            toast.error("Salon bookings are coming soon.");
            return;
        }
        if (selectedService && fromDate && toDate) {
            const fromD = new Date(fromDate);
            const toD = new Date(toDate);

            if (isNaN(fromD.getTime()) || isNaN(toD.getTime())) {
                toast.error("Please enter a valid date.");
                return;
            }

            // Validate Year 
            const fromYear = fromD.getFullYear();
            const toYear = toD.getFullYear();
            const currentYear = new Date().getFullYear();

            if (fromYear > currentYear + 2 || toYear > currentYear + 2 || fromYear < 1900 || toYear < 1900) {
                toast.error("Please enter a valid date within the next 2 years.");
                return;
            }

            if (new Date(toDate) < new Date(fromDate)) {
                toast.error("End date cannot be earlier than start date.");
                return;
            }

            onNext({
                serviceId: selectedService,
                serviceName: selectedServiceName,
                fromDate,
                toDate,
                fromTime,
                toTime,
                subModality: hasSubModalityPricing ? subModality : undefined,
                cleaningType: hasComplexPricing ? cleaningType : undefined,
                supplies: 'No',
            });
        }
    };
    const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newFromDate = e.target.value;
        setFromDate(newFromDate);

        if (toDate < newFromDate) {
            setToDate(newFromDate);
        }
    };

    return (
        <>
        <Container
            maxWidth="md"
            disableGutters={isMobile}
            sx={{
                mt: { xs: 0, sm: 4 },
                mb: 4,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                px: { xs: 0, sm: 2 }
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 1.5, sm: 3, md: 5 },
                    width: '100%',
                    maxWidth: 600,
                    borderRadius: { xs: 1, sm: 4 },
                    border: { xs: 'none' },
                    borderColor: alpha(theme.palette.primary.main, 0.1),
                    background: { xs: 'linear-gradient(145deg, #ffffff 0%, #fffbfd 100%)', sm: 'linear-gradient(145deg, #ffffff 0%, #fffbfd 100%)' },
                    boxShadow: { xs: 'none', sm: '0 8px 32px rgba(244, 117, 164, 0.08)' },
                    position: 'relative'
                }}
            >
                <IconButton
                    onClick={onBack || (() => navigate(-1))}
                    sx={{
                        position: 'absolute',
                        left: { xs: 8, sm: 16 },
                        top: { xs: 8, sm: 16 },
                        color: theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08)
                        }
                    }}
                >
                    <FaChevronLeft />
                </IconButton>
                <Stack spacing={{ xs: 3, sm: 4 }}>
                    <Box textAlign="center">
                        <Typography
                            variant="h4"
                            fontWeight={800}
                            sx={{
                                fontSize: { xs: '1.6rem', sm: '2.125rem' },
                                mb: 1,
                                background: 'linear-gradient(135deg, #F475A4 0%, #D45E8C 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            Start Your Booking
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            Select a service and date range to get started
                        </Typography>
                    </Box>

                    <Stack spacing={{ xs: 2.5, sm: 3 }}>
                        {/* Service Selection */}
                        <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                            <InputLabel id="service-select-label">Select Service</InputLabel>
                            <Select
                                labelId="service-select-label"
                                id="service-select"
                                value={selectedService}
                                label="Select Service"
                                onChange={handleServiceChange}
                                disabled={isLoading}
                                sx={{
                                    borderRadius: 3,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: alpha(theme.palette.primary.main, 0.2)
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main
                                    }
                                }}
                            >
                                {services.map((service) => {
                                    // Salon is temporarily frozen: keep it visible but not selectable.
                                    const isSalonFrozen = /salon/i.test(service.name || '');
                                    return (
                                        <MenuItem key={service._id} value={service._id} disabled={isSalonFrozen}>
                                            {service.name}{isSalonFrozen ? ' (Coming soon)' : ''}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>

                        {/* Cooking/Salon Service Type Selection — Card Grid */}
                        {hasSubModalityPricing && (
                            <>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Pricing Option
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                    {subModalityOptions.map((option) => {
                                        const rawValue = pricingConfiguration?.subModality?.[option];
                                        const isObj = typeof rawValue === 'object' && rawValue !== null;
                                        const base = isObj ? (rawValue.base ?? rawValue.price ?? rawValue.rate ?? 0) : (rawValue ?? 0);
                                        const extra = isObj ? (rawValue.extra ?? 0) : 0;
                                        const includedServices = isObj ? rawValue.includedServices : null;
                                        const isSelected = subModality === option;
                                        return (
                                            <Paper
                                                key={option}
                                                onClick={() => { setDrawerOption(option); setDrawerOpen(true); }}
                                                elevation={0}
                                                sx={{
                                                    p: 1.5,
                                                    minWidth: 140,
                                                    flex: '1 1 auto',
                                                    cursor: 'pointer',
                                                    border: `2px solid ${isSelected ? theme.palette.primary.main : '#e0e0e0'}`,
                                                    borderRadius: 2,
                                                    transition: 'all 0.2s ease',
                                                    bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : '#fff',
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                        boxShadow: '0 2px 8px rgba(244, 117, 164, 0.12)',
                                                    },
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                    {option}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 700 }}>
                                                    C$ {base}{Number(extra) > 0 ? ` + C$ ${extra}` : ''}/hr
                                                </Typography>
                                            </Paper>
                                        );
                                    })}
                                </Box>

                                {subModality && (
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 3,
                                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                        }}
                                    >
                                        <LivePriceCalculator
                                            subModality={subModality}
                                            supplies="No"
                                            hours={(() => {
                                                const [h1, m1] = fromTime.split(':').map(Number);
                                                const [h2, m2] = toTime.split(':').map(Number);
                                                return Math.max(2, h2 - h1 + (m2 - m1) / 60);
                                            })()}
                                            persons={1}
                                            pricingConfiguration={pricingConfiguration}
                                            includedServices={
                                                pricingConfiguration?.subModality?.[subModality]?.includedServices || undefined
                                            }
                                        />
                                    </Box>
                                )}
                            </>
                        )}

                        {/* Cleaning Service Type Selection */}
                        {hasComplexPricing && (
                            <>
                                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                    <InputLabel id="cleaning-type-label">Pricing Type</InputLabel>
                                    <Select
                                        labelId="cleaning-type-label"
                                        value={cleaningType}
                                        label="Pricing Type"
                                        onChange={(e) => setCleaningType(e.target.value)}
                                        sx={{
                                            borderRadius: 3,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: alpha(theme.palette.primary.main, 0.2)
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.primary.main
                                            }
                                        }}
                                    >
                                        {cleaningTypeOptions.map((option) => (
                                            <MenuItem key={option} value={option}>{option} — C$ {pricingConfiguration?.no?.[option]}/hr</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {cleaningType && (
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 3,
                                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                        }}
                                    >
                                        <LivePriceCalculator
                                            subModality={cleaningType}
                                            supplies={supplies}
                                            hours={(() => {
                                                const [h1, m1] = fromTime.split(':').map(Number);
                                                const [h2, m2] = toTime.split(':').map(Number);
                                                return Math.max(2, h2 - h1 + (m2 - m1) / 60);
                                            })()}
                                            persons={1}
                                            pricingConfiguration={pricingConfiguration}
                                        />
                                    </Box>
                                )}
                            </>
                        )}


                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            {/* From Date */}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <MobileDatePicker
                                    label="From Date"
                                    value={fromDate ? dayjs(fromDate) : null}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            const formatted = newValue.format('YYYY-MM-DD');
                                            setFromDate(formatted);
                                            if (toDate < formatted) {
                                                setToDate(formatted);
                                            }
                                        }
                                    }}
                                    format="DD-MM-YYYY"
                                    minDate={dayjs(minDate)}
                                    maxDate={dayjs(maxDate)}
                                    slots={{ calendarHeader: CustomCalendarHeader }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: isMobile ? "small" : "medium",
                                            autoComplete: 'off',
                                            InputProps: {
                                                endAdornment: (
                                                    <InputAdornment position="end" sx={{ mr: -1 }}>
                                                        <ArrowDropDownIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                                    </InputAdornment>
                                                ),
                                            },
                                            sx: {
                                                '& .MuiOutlinedInput-root': {
                                                    cursor: "pointer",
                                                    borderRadius: 3,
                                                    '& fieldset': { borderColor: alpha(theme.palette.primary.main, 0.2) },
                                                    '&:hover fieldset': { borderColor: theme.palette.primary.main },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                                                    '& input': { cursor: 'pointer' }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </LocalizationProvider>

                            {/* To Date */}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <MobileDatePicker
                                    label="To Date"
                                    value={toDate ? dayjs(toDate) : null}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setToDate(newValue.format('YYYY-MM-DD'));
                                        }
                                    }}
                                    format="DD-MM-YYYY"
                                    minDate={dayjs(fromDate)}
                                    maxDate={dayjs(maxDate)}
                                    slots={{ calendarHeader: CustomCalendarHeader }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: isMobile ? "small" : "medium",
                                            autoComplete: 'off',
                                            InputProps: {
                                                endAdornment: (
                                                    <InputAdornment position="end" sx={{ mr: -1 }}>
                                                        <ArrowDropDownIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                                    </InputAdornment>
                                                ),
                                            },
                                            sx: {
                                                '& .MuiOutlinedInput-root': {
                                                    cursor: "pointer",
                                                    borderRadius: 3,
                                                    '& fieldset': { borderColor: alpha(theme.palette.primary.main, 0.2) },
                                                    '&:hover fieldset': { borderColor: theme.palette.primary.main },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                                                    '& input': { cursor: 'pointer' }
                                                }
                                            }
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            {/* From Time */}
                            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                <InputLabel id="from-time-label">From Time</InputLabel>
                                <Select
                                    labelId="from-time-label"
                                    value={fromTime}
                                    label="From Time"
                                    onChange={(e) => {
                                        const event = { target: { value: e.target.value } } as React.ChangeEvent<HTMLInputElement>;
                                        handleFromTimeChange(event);
                                    }}
                                    sx={{
                                        borderRadius: 3,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: alpha(theme.palette.primary.main, 0.2)
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.primary.main
                                        }
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                                >
                                    {Array.from({ length: 24 * 4 }, (_, i) => {
                                        const hour = Math.floor(i / 4);
                                        const minute = (i % 4) * 15;
                                        const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                                        const slotDt = dayjs(`${fromDate} ${time}`);
                                        const safeLimit = dayjs().add(2, 'hour');
                                        const isDisabled = slotDt.isBefore(safeLimit);
                                        return (
                                            <MenuItem
                                                key={time}
                                                value={time}
                                                disabled={isDisabled}
                                            >
                                                {time}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>

                            {/* To Time */}
                            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                <InputLabel id="to-time-label">To Time</InputLabel>
                                <Select
                                    labelId="to-time-label"
                                    value={toTime}
                                    label="To Time"
                                    onChange={(e) => {
                                        const newToTime = e.target.value as string;
                                        setToTime(newToTime);
                                    }}
                                    disabled={['cleaning', 'housekeeping', 'cleaner', 'maid'].some(keyword =>
                                        selectedServiceName?.toLowerCase().includes(keyword)
                                    )}
                                    sx={{
                                        borderRadius: 3,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: alpha(theme.palette.primary.main, 0.2)
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.primary.main
                                        }
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                                >
                                    {Array.from({ length: 24 * 4 }, (_, i) => {
                                        const hour = Math.floor(i / 4);
                                        const minute = (i % 4) * 15;
                                        const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                                        let isDisabled = false;

                                        // For standard services, validate range duration
                                        const isCleaningOrHousekeeping = ['cleaning', 'housekeeping', 'cleaner', 'maid'].some(keyword =>
                                            selectedServiceName?.toLowerCase().includes(keyword)
                                        );

                                        if (!isCleaningOrHousekeeping) {
                                            if (fromDate === toDate) {
                                                const [fromH, fromM] = fromTime.split(':').map(Number);
                                                const fromTotalMinutes = fromH * 60 + fromM;
                                                const toTotalMinutes = hour * 60 + minute;
                                                const diffMinutes = toTotalMinutes - fromTotalMinutes;

                                                // Minimum 2 hours (120 minutes)
                                                if (diffMinutes < 120 && diffMinutes !== 0) {
                                                    isDisabled = true;
                                                }
                                                // Cannot end before or at start on same day
                                                if (diffMinutes <= 0) {
                                                    isDisabled = true;
                                                }
                                            }
                                        }

                                        return (
                                            <MenuItem
                                                key={time}
                                                value={time}
                                                disabled={isDisabled}
                                            >
                                                {time}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                                {(['cleaning', 'housekeeping', 'cleaner', 'maid'].some(keyword =>
                                    selectedServiceName?.toLowerCase().includes(keyword)
                                )) && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1, mt: 0.5 }}>
                                            Fixed 2-hour initial duration
                                        </Typography>
                                    )}
                            </FormControl>
                        </Stack>

                        {/* Overnight/Multi-day indicator */}
                        {toTime < fromTime && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                    mt: -1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1
                                }}
                            >
                                <Box component="span" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>•</Box>
                                Spans next day
                            </Typography>
                        )}
                    </Stack>

                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleNext}
                        disabled={!selectedService || !fromDate || !toDate}
                        sx={{
                            py: { xs: 1.2, sm: 1.5 },
                            borderRadius: 3,
                            textTransform: 'none',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            fontWeight: 700,
                            boxShadow: '0 8px 24px rgba(244, 117, 164, 0.25)',
                            background: 'linear-gradient(135deg, #F475A4 0%, #E06390 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #E06390 0%, #D45E8C 100%)',
                                boxShadow: '0 12px 32px rgba(244, 117, 164, 0.35)',
                            }
                        }}
                    >
                        Create Booking
                    </Button>
                </Stack>
            </Paper>
        </Container>

        {/* Side Panel Drawer — Step 3 */}
        <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => { setDrawerOpen(false); setDrawerOption(null); }}
            PaperProps={{
                sx: {
                    width: { xs: '85%', sm: 400 },
                    borderTopLeftRadius: 16,
                    borderBottomLeftRadius: 16,
                    p: 3,
                },
            }}
        >
            {drawerOption && (() => {
                const rawValue = pricingConfiguration?.subModality?.[drawerOption];
                const isObj = typeof rawValue === 'object' && rawValue !== null;
                const base = isObj ? (rawValue.base ?? rawValue.price ?? rawValue.rate ?? 0) : (rawValue ?? 0);
                const extra = isObj ? (rawValue.extra ?? 0) : 0;
                const includedServices = isObj ? rawValue.includedServices : null;
                const individualTotal = includedServices?.reduce((sum: number, s: any) => sum + (s.individualPrice || 0), 0) || 0;
                return (
                    <Stack spacing={2}>
                        {/* Header */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {drawerOption}
                            </Typography>
                            <IconButton onClick={() => { setDrawerOpen(false); setDrawerOption(null); }} size="small">
                                <FaTimes />
                            </IconButton>
                        </Stack>

                        {/* Price */}
                        <Box>
                            <Typography variant="body2" sx={{ color: '#555' }}>Rate</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                                C$ {base}{Number(extra) > 0 ? ` + C$ ${extra}` : ''}/hr
                            </Typography>
                        </Box>

                        <Divider />

                        {/* Included Services */}
                        {includedServices && includedServices.length > 0 && (
                            <>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Included Services:
                                </Typography>
                                {includedServices.map((svc: any, i: number) => (
                                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                                        <FaCheck style={{ color: theme.palette.primary.main, fontSize: '0.7rem' }} />
                                        <Typography variant="body2" sx={{ flex: 1 }}>{svc.name}</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            C$ {svc.individualPrice?.toLocaleString()}/hr
                                        </Typography>
                                    </Stack>
                                ))}

                                <Divider />

                                {/* Individual vs Package Comparison */}
                                {includedServices.length > 1 && individualTotal > 0 && (
                                    <>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" sx={{ color: '#555' }}>Total (Individual)</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>C$ {individualTotal.toLocaleString()}/hr</Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>Package Price</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>C$ {base.toLocaleString()}/hr</Typography>
                                        </Stack>
                                        {individualTotal - Number(base) > 0 && (
                                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha('#4caf50', 0.08), border: `1px solid ${alpha('#4caf50', 0.2)}`, textAlign: 'center' }}>
                                                <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 700 }}>
                                                    You Save C$ {(individualTotal - Number(base)).toLocaleString()}/hr
                                                </Typography>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {/* Select Button */}
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => {
                                setSubModality(drawerOption);
                                setDrawerOpen(false);
                                setDrawerOption(null);
                            }}
                            sx={{
                                mt: 1,
                                py: 1.2,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 700,
                                background: subModality === drawerOption
                                    ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                                    : 'linear-gradient(135deg, #F475A4 0%, #E06390 100%)',
                                '&:hover': {
                                    background: subModality === drawerOption
                                        ? 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)'
                                        : 'linear-gradient(135deg, #E06390 0%, #D45E8C 100%)',
                                },
                            }}
                        >
                            {subModality === drawerOption ? '✓ Selected' : 'Select Package'}
                        </Button>
                    </Stack>
                );
            })()}
        </Drawer>
        </>
    );
};

export default ServiceDateSelection;
